import React, { useState } from 'react';

const css = {
    wrapper: {
        maxWidth: '600px',
        margin: '40px auto',
        padding: '30px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 0 10px rgba(0,0,0,0.08)',
        fontFamily: 'Arial, sans-serif'
    },
    title: {
        fontSize: '24px',
        marginBottom: '20px',
        fontWeight: 'bold'
    },
    description: {
        fontSize: '14px',
        color: '#333',
        marginBottom: '20px'
    },
    field: {
        marginBottom: '16px'
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 'bold',
        marginBottom: '6px'
    },
    input: {
        width: '100%',
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px'
    },
    twoCol: {
        display: 'flex',
        gap: '10px'
    },
    error: {
        backgroundColor: '#ffe6e6',
        color: '#cc0000',
        padding: '10px',
        marginBottom: '20px',
        borderRadius: '4px',
        fontSize: '14px'
    },
    mandateText: {
        fontSize: '13px',
        color: '#555',
        border: '1px solid #ddd',
        padding: '15px',
        borderRadius: '5px',
        backgroundColor: '#fafafa',
        marginBottom: '20px'
    },
    checkboxContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        fontSize: '13px',
        marginBottom: '20px'
    },
    checkbox: {
        marginRight: '10px',
        marginTop: '2px'
    },
    button: {
        backgroundColor: '#28a745',
        color: '#fff',
        border: 'none',
        padding: '12px 20px',
        fontSize: '16px',
        borderRadius: '4px',
        cursor: 'pointer',
        width: '100%'
    },
    buttonDisabled: {
        backgroundColor: '#aaa',
        cursor: 'not-allowed'
    },
    footer: {
        fontSize: '11px',
        color: '#999',
        textAlign: 'center',
        marginTop: '25px'
    }
};

export default function BacsMandate() {
    const [form, setForm] = useState({
        email: '',
        name: '',
        sortCode: '',
        accountNumber: '',
        addressLine1: '',
        addressCity: '',
        postcode: '',
        accepted: false
    });

    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = e => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        if (!form.accepted) {
            setError('You must agree to the mandate terms before proceeding.');
            setSubmitting(false);
            return;
        }

        // 👉 TODO: Submit `form` data to your backend to create a BACS SetupIntent and attach bank details
        console.log('Form submitted:', form);

        // Simulate submission delay
        setTimeout(() => {
            setSubmitting(false);
            alert('Mandate setup submitted. Redirect or show confirmation.');
        }, 1000);
    };

    return (
        <div style={css.wrapper}>
            <h1 style={css.title}>Set up Bacs Direct Debit</h1>
            <p style={css.description}>
                Enter your UK bank account details below to set up a Direct Debit mandate. Payments
                will only be collected when due, and you’ll be notified in advance.
            </p>

            <form onSubmit={handleSubmit}>
                <div style={css.field}>
                    <label style={css.label}>Email</label>
                    <input
                        type="email"
                        name="email"
                        style={css.input}
                        value={form.email}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div style={css.field}>
                    <label style={css.label}>Name on account</label>
                    <input
                        type="text"
                        name="name"
                        style={css.input}
                        value={form.name}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div style={css.twoCol}>
                    <div style={{ ...css.field, flex: 1 }}>
                        <label style={css.label}>Sort Code</label>
                        <input
                            type="text"
                            name="sortCode"
                            style={css.input}
                            value={form.sortCode}
                            onChange={handleChange}
                            placeholder="20-00-00"
                            required
                        />
                    </div>
                    <div style={{ ...css.field, flex: 1 }}>
                        <label style={css.label}>Account Number</label>
                        <input
                            type="text"
                            name="accountNumber"
                            style={css.input}
                            value={form.accountNumber}
                            onChange={handleChange}
                            placeholder="00012345"
                            required
                        />
                    </div>
                </div>

                <div style={css.field}>
                    <label style={css.label}>Billing Address Line 1</label>
                    <input
                        type="text"
                        name="addressLine1"
                        style={css.input}
                        value={form.addressLine1}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div style={css.twoCol}>
                    <div style={{ ...css.field, flex: 1 }}>
                        <label style={css.label}>Town/City</label>
                        <input
                            type="text"
                            name="addressCity"
                            style={css.input}
                            value={form.addressCity}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div style={{ ...css.field, flex: 1 }}>
                        <label style={css.label}>Postcode</label>
                        <input
                            type="text"
                            name="postcode"
                            style={css.input}
                            value={form.postcode}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </div>

                <div style={css.checkboxContainer}>
                    <input
                        type="checkbox"
                        name="accepted"
                        style={css.checkbox}
                        checked={form.accepted}
                        onChange={handleChange}
                    />
                    <span>
                        I understand that <strong>School Space</strong> has partnered with Stripe to
                        collect Direct Debits. I confirm that I am the account holder and only
                        person required to authorise debits from this account.
                    </span>
                </div>

                {error && <div style={css.error}>{error}</div>}

                <button
                    type="submit"
                    disabled={submitting}
                    style={{
                        ...css.button,
                        ...(submitting ? css.buttonDisabled : {})
                    }}
                >
                    {submitting ? 'Processing...' : 'Continue to setup confirmation'}
                </button>
            </form>

            <p style={css.footer}>
                Your payments are protected by the{' '}
                <a href="https://www.directdebit.co.uk" target="_blank" rel="noopener noreferrer">
                    Direct Debit Guarantee
                </a>
                .<br />
                Powered by Stripe.
            </p>
        </div>
    );
}
